// Employee-set support preferences — Vision & Strategy §5/§6 + Employee Portal
// & App spec §11 (communication), §16 (return-to-work), §17 (team).
//
// The employee controls their own preferences here — distinct from the
// manager-authored RTW "support preferences" note. Rendered inside the employee
// portal (the `data-surface="employee"` blue palette is inherited from the
// Portal root).
//
// Every §11/§16/§17 field is OPTIONAL: a radio group left unselected means "not
// chosen" (null), and the spec is emphatic the product must not assume a
// decision. So the optional groups collapse by default — the employee opens
// only what is relevant. Writes go to the Phase 0 `POST /api/employee/
// preferences` which partial-merges, so saving one group never clears another.
//
// Copy stays neutral + factual per the §1.4 #8 anti-anthropomorphism guardrail
// — no first-person Maeve voice, no emotional address.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CONTACT_METHODS,
  TOUCHPOINT_CADENCES,
  CONTACT_PERSONS,
  CONTACT_CHANNELS,
  CONTACT_FREQUENCIES,
  WIDER_TEAM_COMMS,
  COLLEAGUE_CONTACTS,
  TEAM_COMMUNICATORS,
  RTW_READINESS,
  RTW_CONTACTS,
  RTW_TEAM_AWARENESS,
  RTW_FIRST_DAY,
  KEY_DATE_AWARENESS,
  TRISTATES,
  type ContactMethod,
  type EmployeeSupportPreferences,
  type SetSupportPreferencesRequest,
  type TouchpointCadence,
} from '@besideu/shared/preferences';
import { api } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { SignificantDatesList } from '@/components/SignificantDatesList.tsx';

// Option label maps. Keyed by the shared enum members so a new enum value is a
// compile error here until it gets a label.
const CONTACT_LABELS: Record<ContactMethod, string> = {
  email: 'Email',
  workspace: 'Workspace message (e.g. Teams/Slack)',
  none: 'Do not contact me directly',
};
const CADENCE_LABELS: Record<TouchpointCadence, string> = {
  default: 'Standard — the full 12-month series',
  lighter: 'Lighter — only the longer-term milestones',
  none: 'None — do not prompt my manager to check in',
};
const CONTACT_PERSON_LABELS = {
  manager: 'My manager',
  hr: 'HR',
  either: 'Either is fine',
  unsure: "I'm not sure yet",
} as const;
const CONTACT_CHANNEL_LABELS = {
  email: 'Email',
  phone: 'Phone',
  teams: 'Teams or similar',
  text: 'Text message',
  in_person: 'In person',
  none: 'No contact for now',
} as const;
const CONTACT_FREQUENCY_LABELS = {
  as_planned: 'As planned',
  less_often: 'Less often',
  only_if_necessary: 'Only if necessary',
  employee_initiates: "I'll get in touch myself",
} as const;
const WIDER_TEAM_LABELS = {
  do_not_tell: "Please don't tell my team",
  share_limited: 'Share limited information',
  ask_me_first: 'Ask me first',
  already_agreed: "We've already agreed what to share",
} as const;
const COLLEAGUE_LABELS = {
  okay: 'Colleagues can contact me',
  close_only: 'Only close colleagues',
  none_for_now: 'No colleague contact for now',
} as const;
const TEAM_COMMUNICATOR_LABELS = {
  manager: 'My manager',
  hr: 'HR',
  either: 'Either',
  unsure: "I'm not sure",
} as const;
const RTW_READINESS_LABELS = {
  yes: "Yes, I'm ready",
  not_yet: 'Not yet',
  unsure: 'Unsure',
} as const;
const RTW_CONTACT_LABELS = {
  manager: 'My manager',
  hr: 'HR',
  either: 'Either',
} as const;
const RTW_TEAM_AWARENESS_LABELS = {
  tell_team: 'My team can be told',
  limited: 'Limited information only',
  do_not_tell_yet: "Don't tell my team yet",
} as const;
const RTW_FIRST_DAY_LABELS = {
  quiet_return: 'A quiet return',
  normal_routine: 'My normal routine',
  manager_first: 'A chat with my manager first',
  unsure: 'Unsure',
} as const;
const KEY_DATE_LABELS = {
  aware_ok: "It's okay for my manager and HR to be aware",
  prefer_not_record: "I'd prefer not to record this",
  decide_later: "I'll decide later",
} as const;
const TRISTATE_LABELS = { yes: 'Yes', no: 'No', unsure: 'Unsure' } as const;

function options<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): readonly { value: T; label: string }[] {
  return values.map((value) => ({ value, label: labels[value] }));
}

export function SupportPreferencesCard({
  preferences,
}: {
  preferences: EmployeeSupportPreferences;
}) {
  const queryClient = useQueryClient();
  // Seed the form from the full current preference set (the 3 required fields +
  // every §11/§16/§17 field, which may be null = not chosen).
  // Seed from the current preferences, but DROP `updatedAt` — it's part of
  // `EmployeeSupportPreferences` (the read shape) but not of the write schema,
  // which is `.strict()`, so posting it would 400 the whole save.
  const [form, setForm] = useState<SetSupportPreferencesRequest>(() => {
    const { updatedAt: _ignored, ...writable } = preferences;
    return writable;
  });

  const mutation = useMutation({
    mutationFn: (input: SetSupportPreferencesRequest) =>
      api.post<{ data: EmployeeSupportPreferences }>('/api/employee/preferences', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['employee', 'portal'] }),
  });

  const set = (patch: Partial<SetSupportPreferencesRequest>) =>
    setForm((f) => ({ ...f, ...patch }));

  // `none` cadence and opt-out mean the same thing (no touch-point prompts);
  // keep them consistent so the employee can't submit a contradictory pair.
  function setCadence(touchpointCadence: TouchpointCadence) {
    // Read `touchpointOptIn` off the functional-updater arg `f`, not the
    // render-closure `form`, so a same-tick update can't resurrect a stale value.
    setForm((f) => ({
      ...f,
      touchpointCadence,
      touchpointOptIn: touchpointCadence === 'none' ? false : f.touchpointOptIn,
    }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(form);
  }

  return (
    <Card as="section">
      <h2 className="text-lg font-semibold">Your preferences</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        You are in control of these, and you do not need to fill anything in. Your manager and HR
        can see your contact and support preferences so they can support you the way you have
        chosen. Your private conversations with Maeve are not shared with your manager.
      </p>

      <form className="mt-5 space-y-6" onSubmit={onSubmit}>
        {/* §11.1 — how the organisation should get in touch. Shown by default. */}
        <Radio
          legend="Who would you prefer to hear from?"
          name="preferredContactPerson"
          options={options(CONTACT_PERSONS, CONTACT_PERSON_LABELS)}
          value={form.preferredContactPerson}
          onChange={(preferredContactPerson) => set({ preferredContactPerson })}
        />
        <Radio
          legend="If they get in touch, how would you prefer it?"
          name="preferredContactChannel"
          options={options(CONTACT_CHANNELS, CONTACT_CHANNEL_LABELS)}
          value={form.preferredContactChannel}
          onChange={(preferredContactChannel) => set({ preferredContactChannel })}
        />
        <Radio
          legend="How often?"
          name="contactFrequency"
          options={options(CONTACT_FREQUENCIES, CONTACT_FREQUENCY_LABELS)}
          value={form.contactFrequency}
          onChange={(contactFrequency) => set({ contactFrequency })}
        />

        {/* §11.1 / §17 — wider team. Optional, collapsed by default. */}
        <Group summary="Your wider team">
          <Radio
            legend="What can your team be told?"
            name="widerTeamComms"
            options={options(WIDER_TEAM_COMMS, WIDER_TEAM_LABELS)}
            value={form.widerTeamComms}
            onChange={(widerTeamComms) => set({ widerTeamComms })}
          />
          <BooleanRadio
            legend="Can your team be told your relationship to the person who died?"
            name="shareRelationshipWithTeam"
            value={form.shareRelationshipWithTeam}
            onChange={(shareRelationshipWithTeam) => set({ shareRelationshipWithTeam })}
          />
          <Radio
            legend="Can colleagues contact you?"
            name="colleagueContact"
            options={options(COLLEAGUE_CONTACTS, COLLEAGUE_LABELS)}
            value={form.colleagueContact}
            onChange={(colleagueContact) => set({ colleagueContact })}
          />
          <Radio
            legend="Who should let the team know, if anyone?"
            name="teamCommunicator"
            options={options(TEAM_COMMUNICATORS, TEAM_COMMUNICATOR_LABELS)}
            value={form.teamCommunicator}
            onChange={(teamCommunicator) => set({ teamCommunicator })}
          />
        </Group>

        {/* §16.2 — returning to work. Optional, collapsed by default. */}
        <Group summary="Returning to work">
          <Radio
            legend="Are you ready to talk about returning to work?"
            name="rtwReadiness"
            options={options(RTW_READINESS, RTW_READINESS_LABELS)}
            value={form.rtwReadiness}
            onChange={(rtwReadiness) => set({ rtwReadiness })}
          />
          <Radio
            legend="Who would you prefer to discuss it with?"
            name="rtwContact"
            options={options(RTW_CONTACTS, RTW_CONTACT_LABELS)}
            value={form.rtwContact}
            onChange={(rtwContact) => set({ rtwContact })}
          />
          <Radio
            legend="What can your team be told about your return?"
            name="rtwTeamAwareness"
            options={options(RTW_TEAM_AWARENESS, RTW_TEAM_AWARENESS_LABELS)}
            value={form.rtwTeamAwareness}
            onChange={(rtwTeamAwareness) => set({ rtwTeamAwareness })}
          />
          <Radio
            legend="How would you like your first day back to go?"
            name="rtwFirstDay"
            options={options(RTW_FIRST_DAY, RTW_FIRST_DAY_LABELS)}
            value={form.rtwFirstDay}
            onChange={(rtwFirstDay) => set({ rtwFirstDay })}
          />
          <Radio
            legend="Do you have any concerns about your workload?"
            name="rtwWorkloadConcerns"
            options={options(TRISTATES, TRISTATE_LABELS)}
            value={form.rtwWorkloadConcerns}
            onChange={(rtwWorkloadConcerns) => set({ rtwWorkloadConcerns })}
          />
          <Radio
            legend="Do you think you will need some flexibility?"
            name="rtwFlexibilityNeeded"
            options={options(TRISTATES, TRISTATE_LABELS)}
            value={form.rtwFlexibilityNeeded}
            onChange={(rtwFlexibilityNeeded) => set({ rtwFlexibilityNeeded })}
          />
          <BooleanRadio
            legend="Would you like HR to support the conversation?"
            name="rtwHrSupportRequested"
            value={form.rtwHrSupportRequested}
            onChange={(rtwHrSupportRequested) => set({ rtwHrSupportRequested })}
          />
        </Group>

        {/* §11.1 — key dates. Optional, collapsed by default. */}
        <Group summary="Significant dates">
          <Radio
            legend="Anniversaries, birthdays or other dates that may be difficult"
            name="keyDateAwareness"
            options={options(KEY_DATE_AWARENESS, KEY_DATE_LABELS)}
            value={form.keyDateAwareness}
            onChange={(keyDateAwareness) => set({ keyDateAwareness })}
          />
          <SignificantDatesList keyDateAwareness={form.keyDateAwareness ?? null} />
        </Group>

        {/* Platform reminders — the original 3 fields. Govern besideU's own
            messages + the touch-point reminder cadence. */}
        <Group summary="besideU reminders">
          <Radio
            legend="How should besideU itself message you?"
            name="preferredContactMethod"
            options={options(CONTACT_METHODS, CONTACT_LABELS)}
            value={form.preferredContactMethod}
            onChange={(preferredContactMethod) => set({ preferredContactMethod })}
          />
          <fieldset>
            <legend className="text-sm font-medium">Touch-point reminders</legend>
            <p className="mt-1 text-xs text-muted-foreground">
              Touch points prompt your manager to check in at key moments. Choose how often.
            </p>
            <div className="mt-2 space-y-2">
              {TOUCHPOINT_CADENCES.map((cadence) => (
                <label key={cadence} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="touchpointCadence"
                    value={cadence}
                    checked={form.touchpointCadence === cadence}
                    onChange={() => setCadence(cadence)}
                  />
                  {CADENCE_LABELS[cadence]}
                </label>
              ))}
            </div>
          </fieldset>
        </Group>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save preferences'}
          </Button>
          {mutation.isSuccess && !mutation.isPending && (
            <span className="text-sm text-muted-foreground" role="status">
              Saved.
            </span>
          )}
          {mutation.isError && (
            <span className="text-sm text-traffic-red" role="alert">
              Could not save. Try again.
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

// A radio group for a single nullable preference. Unselected = "not chosen".
function Radio<T extends string>({
  legend,
  name,
  options: opts,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  options: readonly { value: T; label: string }[];
  value: T | null | undefined;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium">{legend}</legend>
      <div className="mt-2 space-y-2">
        {opts.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// Yes/No radios for a nullable boolean. Unselected = "not chosen".
function BooleanRadio({
  legend,
  name,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  value: boolean | null | undefined;
  onChange: (v: boolean) => void;
}) {
  const current = value === true ? 'yes' : value === false ? 'no' : null;
  return (
    <Radio
      legend={legend}
      name={name}
      options={[
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ]}
      value={current}
      onChange={(v) => onChange(v === 'yes')}
    />
  );
}

// A collapsible optional group — keeps the form calm by default (§9.2).
function Group({ summary, children }: { summary: string; children: React.ReactNode }) {
  return (
    <details className="rounded-md border border-border">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium">{summary}</summary>
      <div className="space-y-5 border-t border-border px-4 py-4">{children}</div>
    </details>
  );
}
